import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Flex, Input, Modal, Typography } from "djuno-design";

import { AiAppImageSchema } from "../../../../utils/validations";
import {
  handleToggleAiAppImageModal,
  selectAiAppImageEditor,
  selectAiAppsActionLoading,
  selectSelectedAiApp,
  updateAiAppImageAsync,
} from "../../../../store/ai/deploys/appsSlice";
import { AiAppDeployType } from "../../../../types/ai";
import { getAiAppAsync } from "../../../../store/ai/deploys/appSlice";

const AiAppImageModal = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectAiAppImageEditor);
  const loading = useAppSelector(selectAiAppsActionLoading);

  const selectedApp = useAppSelector(selectSelectedAiApp);

  const [selectedDeployType] = useState<AiAppDeployType>("docker-image");

  // const catalogs = useAppSelector(selectDBSCatalogs);
  // const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);

  const {
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(AiAppImageSchema(selectedDeployType)),
    shouldUnregister: true,
    reValidateMode: "onChange",
    defaultValues: {
      dockerImage: "",
      dockerRunCommand: "",
    },
  });

  const dockerImage = watch("dockerImage");
  // const dockerRunCommand = watch("dockerRunCommand");

  useEffect(() => {
    if (selectedApp && isOpen) {
      const _image: string = selectedApp.spec.image;

      setValue("dockerImage", _image || "");
    }
  }, [selectedApp, isOpen]);

  //get first data
  // useEffect(() => {
  //   dispatch(getDBSCatalogsAsync("ai-app"));
  // }, [dispatch]);

  // const catalog = useMemo(() => {
  //   if (!selectedApp && !catalogs) return undefined;
  //   return catalogs?.find((c) =>
  //     c.planCode.startsWith(
  //       "ai-app." + selectedApp?.spec.resources.flavor + ".minute.consumption"
  //     )
  //   );
  // }, [catalogs]);

  const onSubmit = () => {
    if (!loading && selectedApp) {
      const image = dockerImage;
      // let partnerId: string | null = null;
      // if (selectedDeployType === "partner") {
      //   image = selectedImageId + ":" + selectedImageTag;
      //   partnerId = selectedImage?.partnerId || null;
      // }

      dispatch(
        updateAiAppImageAsync({
          id: selectedApp.id,
          data: { url: image },
        })
      ).then((action) => {
        if (action.type === "ai-apps/update/image/fulfilled") {
          handleOnClose();
          dispatch(
            getAiAppAsync({
              id: selectedApp.id,
            })
          );
        }
      });
    }
  };

  const handleOnClose = useCallback(() => {
    reset();
    dispatch(handleToggleAiAppImageModal({ isOpen: false, app: null }));
  }, [dispatch, reset]);

  useEffect(() => {
    return () => {
      handleOnClose();
    };
  }, [handleOnClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      title="Update image"
      contentClassName="max-w-lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Alert showIcon uiType="info" className="my-5">
          <Flex direction="col">
            <Typography.Text className="!text-xs">
              You can update your application's Docker image to offer an
              up-to-date version of your service. Updates are incremental, and
              will not result in any service disruptions. Your current
              configuration will also be retained, such as your HTTP endpoint
              and deployment strategy.
            </Typography.Text>
            <Typography.Text className="!text-xs">
              Please provide a different tag so that the Docker image update is
              taken into account.
            </Typography.Text>
          </Flex>
        </Alert>
        <Controller
          control={control}
          name="dockerImage"
          render={({ field: { value, onChange } }) => (
            <Input
              label="Your Docker image path"
              value={value || ""}
              onChange={onChange}
              placeholder="ai-deploy-hello-world:latest"
              error={errors?.dockerImage?.message as string | undefined}
            />
          )}
        />
        <Typography.Text size="xs">
          Enter the path to your Docker image. This can be a public (GitHub,
          Gitlab, DockerHub, etc.) or private registry.
        </Typography.Text>

        <div className="mt-4 flex justify-end gap-2">
          <Button uiType="light" onClick={handleOnClose}>
            Cancel
          </Button>

          <Button uiType="primary" type="submit" loading={loading}>
            Update
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AiAppImageModal;
